'use client'

import { useState, useRef, useEffect } from 'react'

/**
 * Anima a transição até `target` com easing cúbico (ease-out), ~420ms.
 * Usado nos números-herói dos cartões de resultado.
 */
export function useAnimatedNumber(target: number, duration = 420): number {
  const [val, setVal] = useState(target)
  const fromRef = useRef(target)
  const startRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    fromRef.current = val
    startRef.current = null
    const from = fromRef.current
    const delta = target - from

    if (Math.abs(delta) < 0.005) {
      setVal(target)
      return
    }

    const tick = (t: number) => {
      if (startRef.current === null) startRef.current = t
      const pr = Math.min((t - startRef.current) / duration, 1)
      setVal(from + delta * (1 - Math.pow(1 - pr, 3)))
      if (pr < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration])

  return val
}
